import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { fileUrl, num, advRange } from "../api";
import { useT } from "../i18n";

// Данные магазина: телефоны в наличии и контакты. Загружаются один раз на весь сайт.
const ShopCtx = createContext(null);

export const ShopProvider = ({ children }) => {
    const [state, setState] = useState({ phones: null, info: null, error: false });
    const [open, setOpen] = useState(null); // id телефона в окне
    const load = useCallback(() => {
        Promise.all([api.get("/phones"), api.get("/info")])
            .then(([p, i]) => setState({ phones: p.data.phones, info: i.data, error: false }))
            .catch(() => setState((s) => ({ ...s, error: true })));
    }, []);
    useEffect(() => { load(); }, [load]);
    return (
        <ShopCtx.Provider value={{ ...state, open: setOpen, reload: load }}>
            {children}
            {open && state.phones && <PhoneModal phone={state.phones.find((p) => p.id === open)} onClose={() => setOpen(null)} />}
        </ShopCtx.Provider>
    );
};
export const useShop = () => useContext(ShopCtx);

export const PhoneIcon = () => <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M8 2h8a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zM11 18h2" /></svg>;
export const CallIcon = () => <svg viewBox="0 0 24 24"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" /></svg>;
const BatIcon = () => <svg viewBox="0 0 24 24"><path d="M3 8h15v8H3zM20 11v2" /></svg>;

export const Photo = ({ phone }) =>
    phone.photo ? <img src={fileUrl(phone.photo)} alt={`${phone.model} ${phone.color}`} loading="lazy" /> : <span className="noph"><PhoneIcon /></span>;

export const CallButton = ({ className = "btn", children }) => {
    const t = useT();
    return <a className={className} href="tel:+998771167000"><CallIcon />{children || t("cta_call")}</a>;
};

// Соцсети магазина
export const SOCIALS = [
    ["Instagram", "https://www.instagram.com/tele.store.nukus"],
    ["Telegram", "https://t.me/telestorenukus"],
    ["TikTok", "https://www.tiktok.com/@tele.store.nukus"],
];
export const Socials = () => (
    <span className="socials">
        {SOCIALS.map(([name, url]) => <a key={name} href={url} target="_blank" rel="noopener noreferrer">{name}</a>)}
    </span>
);

export const PhoneCard = ({ p }) => {
    const t = useT();
    const shop = useShop();
    const money = (n) => num(n) + " " + t("sum");
    return (
        <button className="card" onClick={() => shop.open(p.id)}>
            <div className="im"><Photo phone={p} />
                {p.battery != null && <span className={`bat ${p.battery < 90 ? "mid" : ""}`}><BatIcon />{p.battery}%</span>}
            </div>
            <div className="bd">
                <h3>{p.model}</h3>
                <span className="spec">{p.memory} · {p.color}{p.simCount === 2 ? " · 2 SIM" : ""}</span>
                <span className="pr num">{money(p.price)}</span>
                <span className="mo">{(t("from") ? t("from") + " " : "") + money(p.fromMonthly) + t("per_mo")}</span>
            </div>
        </button>
    );
};

/* Калькулятор рассрочки: платёж считает сервер, наценка и процент на сайт не попадают */
export const Calc = ({ phone, selectable }) => {
    const t = useT();
    const shop = useShop();
    const months = shop?.info?.months?.length ? shop.info.months : [3, 6, 9, 12];
    const [cur, setCur] = useState(phone);
    const [m, setM] = useState(months[months.length - 1]);
    const range = cur ? advRange(cur.price, shop?.info) : { min: 0, max: 0 };
    const [adv, setAdv] = useState(range.min);
    const [res, setRes] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => { setCur(phone); }, [phone]);
    useEffect(() => { setAdv(range.min); }, [cur, range.min]); // новый телефон → минимальный аванс
    const clamp = (v) => Math.max(range.min, Math.min(range.max, Math.round(Number(v) || 0)));

    useEffect(() => {
        if (!cur) return;
        setBusy(true);
        const tm = setTimeout(() => {
            api.get("/calc", { params: { phoneId: cur.id, advance: clamp(adv), months: m } })
                .then((r) => setRes({ monthly: r.data.monthly }))
                .catch((e) => setRes({ error: e.response?.status === 404 ? t("sold_out") : t("load_err") }))
                .finally(() => setBusy(false));
        }, 250);
        return () => clearTimeout(tm);
    }, [cur, adv, m]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!cur) return null;
    const money = (n) => num(n) + " " + t("sum");
    const sorted = selectable ? [...shop.phones].sort((a, b) => a.model.localeCompare(b.model, undefined, { numeric: true })) : [];
    return (
        <>
            {selectable && (
                <label className="fld"><span>{t("calc_phone")}</span>
                    <div className="calc-ph">
                        {cur.photo ? <img src={fileUrl(cur.photo)} alt="" /> : null}
                        <select value={cur.id} onChange={(e) => setCur(shop.phones.find((x) => x.id === Number(e.target.value)))}>
                            {sorted.map((x) => <option key={x.id} value={x.id}>{x.model} {x.memory} · {x.color} — {money(x.price)}</option>)}
                        </select>
                    </div>
                </label>
            )}
            <div className="fld">
                <span>{t("calc_adv")}<b>{money(adv)}</b></span>
                <input type="range" min={range.min} max={range.max} step="100000" value={adv} onChange={(e) => setAdv(clamp(e.target.value))} aria-label={t("calc_adv")} />
                <input type="number" min={range.min} max={range.max} step="100000" value={adv} inputMode="numeric" aria-label={t("calc_adv")}
                    onChange={(e) => setAdv(Number(e.target.value) || 0)} onBlur={(e) => setAdv(clamp(e.target.value))} />
                <span className="lang-hint">{t("adv_range", { min: money(range.min), max: money(range.max) })}</span>
            </div>
            <div className="fld"><span>{t("calc_months")}</span>
                <div className="mseg">
                    {months.map((x) => <button type="button" key={x} className={x === m ? "on" : ""} onClick={() => setM(x)}>{x}<small>{t("mo")}</small></button>)}
                </div>
            </div>
            <div className={`result ${busy ? "calc-loading" : ""}`}><span>{t("calc_monthly")}</span><b>{res?.error ? "—" : res ? money(res.monthly) : "…"}</b></div>
            {res?.error ? <p className="fine" style={{ color: "#b23c2a" }}>{res.error}</p> : <p className="fine">{t("calc_note")}</p>}
        </>
    );
};

/* Окно телефона */
const PhoneModal = ({ phone, onClose }) => {
    const t = useT();
    useEffect(() => {
        const k = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", k);
        document.body.style.overflow = "hidden";
        return () => { document.removeEventListener("keydown", k); document.body.style.overflow = ""; };
    }, [onClose]);
    if (!phone) return null;
    const money = (n) => num(n) + " " + t("sum");
    return (
        <div id="ov" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="modal" role="dialog" aria-modal="true" aria-label={phone.model}>
                <button className="mx" onClick={onClose} aria-label="×">×</button>
                <div className="pd">
                    <div className="pd-img"><Photo phone={phone} /></div>
                    <div className="pd-info">
                        <h2>{phone.model}</h2>
                        <div className="specs">
                            <div><small>{t("mem")}</small><b>{phone.memory}</b></div>
                            <div><small>{t("color")}</small><b>{phone.color}</b></div>
                            <div><small>{t("bat")}</small><b>{phone.battery == null ? "—" : phone.battery + "%"}</b></div>
                        </div>
                        <div className="pd-price num"><small>{t("price")}</small>{money(phone.price)}</div>
                        <div className="calc"><Calc phone={phone} /></div>
                        <div className="callbox">
                            <CallButton />
                            <p>{t("call_note", { p: "" })}<b>+998 77 116 70 00</b></p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
